import React, { useCallback, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { createPage, deletePage, getAdminPages, updatePage } from '../../services/admin/pages';
import { REQUIRED_LEGAL_PAGES, REQUIRED_LEGAL_PAGE_SLUGS } from '../../constants/legalPages';
import useAdminAutoRefresh from '../../hooks/useAdminAutoRefresh';

const emptyForm = {
  id: null,
  slug: '',
  title: '',
  content: '',
  category: '',
  is_active: true,
};

const PagesPage = () => {
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchPages = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getAdminPages();
      setPages(data);
    } catch (error) {
      console.error('Error fetching pages:', error);
      if (!silent) {
        toast({
          title: 'Hata',
          description: 'Sayfalar yüklenirken hata oluştu.',
          variant: 'destructive',
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast]);

  useAdminAutoRefresh(fetchPages, { interval: 15000, enabled: !isDialogOpen });

  const handleSave = async () => {
    try {
      if (form.id) {
        await updatePage(form.id, form);
      } else {
        await createPage(form);
      }
      setIsDialogOpen(false);
      setForm(emptyForm);
      await fetchPages();
      toast({
        title: 'Başarılı',
        description: 'Sayfa kaydedildi.',
      });
    } catch (error) {
      console.error('Error saving page:', error);
      const apiMessage = error.response?.data?.detail;
      toast({
        title: 'Hata',
        description:
          typeof apiMessage === 'string' ? apiMessage : 'Sayfa kaydedilirken hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (pageId) => {
    const targetPage = pages.find((page) => page.id === pageId);
    const isRequired = targetPage && REQUIRED_LEGAL_PAGE_SLUGS.includes(targetPage.slug);
    if (isRequired) {
      const requiredDeleteApproval = window.confirm(
        `"${targetPage.title}" zorunlu uyumluluk sayfasıdır. Silmek istediğinize emin misiniz?`
      );
      if (!requiredDeleteApproval) {
        return;
      }
    }
    const deleteApproval = window.confirm('Bu sayfayı silmek istediğinize emin misiniz?');
    if (!deleteApproval) {
      return;
    }

    try {
      await deletePage(pageId);
      await fetchPages();
      toast({
        title: 'Başarılı',
        description: 'Sayfa silindi.',
      });
    } catch (error) {
      console.error('Error deleting page:', error);
      toast({
        title: 'Hata',
        description: 'Sayfa silinirken hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const handleBootstrapRequiredPages = async () => {
    try {
      setBootstrapping(true);
      const existingSlugs = new Set(pages.map((page) => page.slug));
      const missingPages = REQUIRED_LEGAL_PAGES.filter((page) => !existingSlugs.has(page.slug));

      if (missingPages.length === 0) {
        toast({
          title: 'Hazır',
          description: 'Zorunlu yasal sayfaların tamamı zaten mevcut.',
        });
        return;
      }

      await Promise.all(
        missingPages.map((page) =>
          createPage({
            slug: page.slug,
            title: page.title,
            content: page.content,
            category: page.category,
            is_active: true,
          })
        )
      );

      await fetchPages();
      toast({
        title: 'Başarılı',
        description: `${missingPages.length} zorunlu sayfa oluşturuldu.`,
      });
    } catch (error) {
      console.error('Error bootstrapping required pages:', error);
      const apiMessage = error.response?.data?.detail;
      toast({
        title: 'Hata',
        description:
          typeof apiMessage === 'string'
            ? apiMessage
            : 'Zorunlu sayfalar oluşturulurken hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setBootstrapping(false);
    }
  };

  const requiredPageStatus = REQUIRED_LEGAL_PAGES.map((requiredPage) => {
    const foundPage = pages.find((page) => page.slug === requiredPage.slug);
    return {
      key: requiredPage.key,
      slug: requiredPage.slug,
      title: requiredPage.title,
      exists: !!foundPage,
      isActive: !!foundPage?.is_active,
    };
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">İçerik Sayfaları</h1>
            <p className="text-gray-600 mt-1">Footer ve özel sayfaları yönetin.</p>
          </div>
          <div className="admin-toolbar">
            <Button variant="outline" onClick={handleBootstrapRequiredPages} disabled={bootstrapping}>
              {bootstrapping ? 'Oluşturuluyor...' : 'Zorunlu Sayfaları Oluştur'}
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setForm(emptyForm)}>Yeni Sayfa</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{form.id ? 'Sayfayı Düzenle' : 'Yeni Sayfa'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Başlık</Label>
                      <Input
                        id="title"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="slug">Slug</Label>
                      <Input
                        id="slug"
                        value={form.slug}
                        onChange={(e) => setForm({ ...form, slug: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="category">Kategori</Label>
                    <Input
                      id="category"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      placeholder="customer_service / shopping / legal"
                    />
                  </div>
                  <div>
                    <Label htmlFor="content">İçerik (HTML)</Label>
                    <Textarea
                      id="content"
                      rows={10}
                      value={form.content}
                      onChange={(e) => setForm({ ...form, content: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                      İptal
                    </Button>
                    <Button onClick={handleSave}>Kaydet</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-4">
          <h2 className="text-lg font-semibold mb-3">Ödeme Entegrasyonu Uyum Durumu</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {requiredPageStatus.map((item) => (
              <div key={item.key} className="rounded-md border p-3">
                <p className="font-medium">{item.title}</p>
                <p className="text-sm text-gray-500">/pages/{item.slug}</p>
                <div className="mt-2 text-sm flex items-center gap-3">
                  <span className={item.exists ? 'text-green-700' : 'text-red-600'}>
                    {item.exists ? 'Mevcut' : 'Eksik'}
                  </span>
                  <span className={item.isActive ? 'text-green-700' : 'text-amber-600'}>
                    {item.exists ? (item.isActive ? 'Aktif' : 'Pasif') : 'Bekliyor'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-gray-600">Yükleniyor...</div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">Başlık</th>
                  <th className="px-4 py-3 text-left">Slug</th>
                  <th className="px-4 py-3 text-left">Kategori</th>
                  <th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {pages.map((page) => (
                  <tr key={page.id} className="border-b">
                    <td className="px-4 py-3">{page.title}</td>
                    <td className="px-4 py-3">/{page.slug}</td>
                    <td className="px-4 py-3">{page.category || '-'}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setForm(page);
                          setIsDialogOpen(true);
                        }}
                      >
                        Düzenle
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(page.id)}
                      >
                        Sil
                      </Button>
                    </td>
                  </tr>
                ))}
                {pages.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-gray-500" colSpan={4}>
                      Henüz sayfa yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PagesPage;
